import React from "react";

import Link from "next/link";

import { Button } from "@heroui/button";
import { Card, CardBody, CardFooter } from "@heroui/card";
import {
  CheckCircle,
  Clock,
  Copy,
  Link as LinkIcon,
  MapPin,
  Package,
  Truck,
} from "lucide-react";

import { NeedImage } from "@/components/needs/NeedImage";
import { StatusChip } from "@/components/ui/StatusChip";
import { SPACING } from "@/lib/constants/layoutConstants";
import { getCategoryColor, getDaysRemaining } from "@/lib/need-utils";
import { EventKeys } from "@/lib/observability/catalog";
import { EventPublisher } from "@/lib/observability/events";
import { OrganizationTerminology } from "@/lib/services/terminologyService";
import { capitalizeFirstLetter } from "@/lib/types/common";
import { NeedWithArea } from "@/lib/types/prismaTypes";
import { cn } from "@/lib/utils";

interface ClaimStats {
  totalRequested: number;
  claimed: number;
  remaining: number;
}

function getDueDateColor(
  daysRemaining: number,
): "danger" | "warning" | "primary" | "success" {
  if (daysRemaining < 0) return "danger";
  if (daysRemaining <= 3) return "warning";
  if (daysRemaining <= 7) return "primary";

  return "success";
}

function getDueDateLabel(daysRemaining: number): string {
  if (daysRemaining < 0) return "Overdue";
  if (daysRemaining === 0) return "Due today";

  return `${daysRemaining} day${daysRemaining !== 1 ? "s" : ""} left`;
}

function getDueDateAriaLabel(daysRemaining: number): string {
  if (daysRemaining < 0) return "Overdue";
  if (daysRemaining === 0) return "Due today";

  return `${daysRemaining} day${daysRemaining !== 1 ? "s" : ""} remaining`;
}

function formatNeedTypeAmount(
  needType: string | undefined,
  count: number,
  total: number,
  volunteerPlural: string,
): string {
  if (needType === "FINANCIAL_ASSISTANCE") {
    return `$${(count / 100).toFixed(2)} raised of $${(total / 100).toFixed(2)} goal`;
  }
  if (needType === "PEOPLE_NEEDED") {
    return `${count} of ${total} ${volunteerPlural} committed`;
  }

  return `${count} of ${total} claimed`;
}

function formatRemainingAmount(
  needType: string | undefined,
  remaining: number,
  volunteerPlural: string,
): string {
  if (needType === "FINANCIAL_ASSISTANCE") {
    return ` ($${(remaining / 100).toFixed(2)} still needed)`;
  }
  if (needType === "PEOPLE_NEEDED") {
    return ` (${remaining} ${volunteerPlural} still needed)`;
  }

  return ` (${remaining} still needed)`;
}

function formatTotalNeeded(
  needType: string | undefined,
  total: number,
  volunteerPlural: string,
): string {
  if (needType === "FINANCIAL_ASSISTANCE")
    return `$${(total / 100).toFixed(2)} needed`;
  if (needType === "PEOPLE_NEEDED") return `${total} ${volunteerPlural} needed`;

  return `${total} items needed`;
}

function formatClaimStatsLabel(
  needType: string | undefined,
  stats: ClaimStats,
  volunteerPlural: string,
): React.ReactNode {
  if (stats.claimed === 0) {
    return formatTotalNeeded(needType, stats.totalRequested, volunteerPlural);
  }

  const claimedText = formatNeedTypeAmount(
    needType,
    stats.claimed,
    stats.totalRequested,
    volunteerPlural,
  );
  const remainingText =
    stats.remaining > 0
      ? formatRemainingAmount(needType, stats.remaining, volunteerPlural)
      : null;

  return (
    <>
      {claimedText}
      {remainingText && (
        <span className="text-success-600 ml-1">{remainingText}</span>
      )}
    </>
  );
}

function formatQuantityLabel(
  needType: string | undefined,
  targetQuantity: number,
  volunteerPlural: string,
): string {
  if (needType === "FINANCIAL_ASSISTANCE") {
    return `$${(targetQuantity / 100).toFixed(2)} needed`;
  }
  if (needType === "PEOPLE_NEEDED") {
    return `${targetQuantity} ${volunteerPlural} needed`;
  }

  return `${targetQuantity} ${targetQuantity === 1 ? "item" : "items"}`;
}

interface NeedCardProps {
  need: NeedWithArea;
  orgSlug: string;
  onCategoryClick: (categoryId: string) => void;
  claimStats?: {
    totalRequested: number;
    claimed: number;
    remaining: number;
  };
  variant?: "active" | "completed";
  terminology: OrganizationTerminology;
}

/**
 * Card component for displaying a need in the needs list
 * Features enhanced accessibility including:
 * - Proper keyboard navigation
 * - ARIA roles and labels
 * - Screen reader support
 * - Focus management
 */
function renderTagsList(tags: NeedWithArea["tags"]) {
  if (!tags || tags.length === 0) return null;

  return (
    <div className="mt-2">
      <p className="text-xs text-default-600 mb-1">Tags:</p>
      <div className="flex flex-wrap gap-1">
        {tags.map((tag) => (
          <span
            key={tag.id}
            className="text-xs bg-default-100 text-default-700 px-2 py-0.5 rounded-full"
          >
            {tag.name}
          </span>
        ))}
      </div>
    </div>
  );
}

function getQuantityClassName(
  claimStats: ClaimStats | undefined,
  isCompleted: boolean,
): string | undefined {
  const hasClaims = (claimStats?.claimed ?? 0) > 0;

  if (!hasClaims) return undefined;

  return `font-medium ${isCompleted ? "text-success-600" : "text-primary-600"}`;
}

function renderQuantityItem(
  need: NeedWithArea,
  claimStats: ClaimStats | undefined,
  isCompleted: boolean,
  volunteerPlural: string,
) {
  // A missing targetQuantity means an implicit quantity of 1, same as the
  // rest of the app (lib/actions/claims.ts, lib/actions/offers.ts, etc.) —
  // it must not hide the claim indicator entirely.
  const targetQuantity = need.targetQuantity ?? 1;

  return (
    <div
      className="flex items-center gap-1 text-xs text-foreground"
      role="listitem"
    >
      <Package aria-hidden="true" className="w-4 h-4" />
      <span className={getQuantityClassName(claimStats, isCompleted)}>
        {claimStats
          ? formatClaimStatsLabel(need.needType, claimStats, volunteerPlural)
          : formatQuantityLabel(need.needType, targetQuantity, volunteerPlural)}
      </span>
    </div>
  );
}

function renderShippingItem(shippingAddress: string | null | undefined) {
  if (shippingAddress) {
    return (
      <div
        className="flex items-center gap-1 text-xs text-foreground"
        role="listitem"
      >
        <Truck aria-hidden="true" className="w-4 h-4" />
        <span>Shipping Available</span>
      </div>
    );
  }

  return (
    <div
      className="flex items-center gap-1 text-xs text-foreground"
      role="listitem"
    >
      <MapPin aria-hidden="true" className="w-4 h-4" />
      <span>Dropoff Required</span>
    </div>
  );
}

function hasHelpfulLinks(helpfulLinks: NeedWithArea["helpfulLinks"]): boolean {
  return Boolean(
    helpfulLinks && Array.isArray(helpfulLinks) && helpfulLinks.length > 0,
  );
}

function renderNeedMetadata(
  need: NeedWithArea,
  claimStats: ClaimStats | undefined,
  isCompleted: boolean,
  needId: string,
  terminology: OrganizationTerminology,
) {
  return (
    <div
      aria-describedby={`${needId}-description`}
      aria-label="Need details"
      className="grid grid-cols-2 gap-2 mb-2"
      role="list"
    >
      <div
        className="flex items-center gap-1 text-xs text-foreground"
        role="listitem"
      >
        <MapPin aria-hidden="true" className="w-4 h-4" />
        <span>
          {terminology.area.singular}: {need.area.name}
        </span>
      </div>

      {renderTagsList(need.tags)}
      {renderQuantityItem(
        need,
        claimStats,
        isCompleted,
        terminology.volunteer.plural,
      )}
      {renderShippingItem(need.shippingAddress)}

      {hasHelpfulLinks(need.helpfulLinks) && (
        <div
          className="flex items-center gap-1 text-xs text-foreground"
          role="listitem"
        >
          <LinkIcon aria-hidden="true" className="w-4 h-4" />
          <span>Links Available</span>
        </div>
      )}

      {need.isDuplicated && (
        <div
          className="flex items-center gap-1 text-xs text-foreground"
          role="listitem"
        >
          <Copy aria-hidden="true" className="w-4 h-4" />
          <span>Duplicated</span>
        </div>
      )}
    </div>
  );
}

function renderCategoryBadge(
  category: NeedWithArea["category"],
  onCategoryClick: (id: string) => void,
) {
  if (!category) return null;

  return (
    <div className="absolute bottom-2 left-2">
      <StatusChip
        aria-label={`Filter by category: ${category.name}`}
        className="text-xs"
        color={getCategoryColor(category.id)}
        role="button"
        tabIndex={0}
        variant="solid"
        onClick={(e: React.MouseEvent) => {
          e.stopPropagation();
          e.preventDefault();
          onCategoryClick(category.id);
        }}
        onKeyDown={(e: React.KeyboardEvent) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            e.stopPropagation();
            onCategoryClick(category.id);
          }
        }}
      >
        {category.name}
      </StatusChip>
    </div>
  );
}

function isInteractiveTarget(target: HTMLElement): boolean {
  return Boolean(target.closest('button, [role="button"], [tabindex="0"]'));
}

function getNeedCardStyles(isCompleted: boolean) {
  return {
    buttonVariant: (isCompleted ? "light" : "solid") as
      | "flat"
      | "solid"
      | "bordered"
      | "light"
      | "faded"
      | "shadow"
      | "ghost",
    cardClass: cn(
      "h-full overflow-hidden hover:shadow-md transition-shadow cursor-pointer relative focus-within:ring-2 focus-within:ring-primary",
      isCompleted && "opacity-75 bg-default-50",
    ),
    descriptionClass: `text-default-600 line-clamp-2 text-sm mb-2 ${isCompleted ? "text-default-500" : ""}`,
    grayscaleClass: isCompleted ? "grayscale" : "",
    titleClass: cn(
      "text-lg font-semibold hover:text-primary-600 line-clamp-2 break-words mb-2",
      isCompleted && "text-default-600",
    ),
  };
}

function NeedCard({
  claimStats,
  need,
  onCategoryClick,
  orgSlug,
  terminology,
  variant,
}: NeedCardProps) {
  const resolvedVariant = variant ?? "active";
  const daysRemaining = getDaysRemaining(need.dueDate);
  const needDetailUrl = `/${orgSlug}/needs/${need.id}`;
  const needId = `need-${need.id}`;
  const isCompleted = resolvedVariant === "completed";
  const {
    buttonVariant,
    cardClass,
    descriptionClass,
    grayscaleClass,
    titleClass,
  } = getNeedCardStyles(isCompleted);
  const imageUrl = need.imageUrl ?? null;

  const handleCardClick = () => {
    void EventPublisher.publishEvent(EventKeys.ORG_NEED_CARD_CLICK, {
      need_area: need.area.name,
      need_category: need.category?.name,
      need_id: need.id,
      need_title: need.title,
      need_urgency: need.urgency,
      organization_slug: orgSlug,
    });
  };

  return (
    <article className="h-full" data-testid="need-card" id={needId}>
      <Card aria-labelledby={`${needId}-title`} className={cardClass}>
        {/* Image with Status Badges */}
        <div
          className={`relative w-full aspect-video overflow-hidden ${grayscaleClass}`}
        >
          <NeedImage
            alt={`Image for ${need.title}`}
            aspectRatio="16:9"
            fill={true}
            need={{
              tags: need.tags,
              urgency: need.urgency,
            }}
            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
            src={imageUrl}
          />
          <div className="absolute top-2 right-2">
            {/* Completed badge */}
            {isCompleted && (
              <StatusChip
                aria-label="Need completed"
                color="success"
                size="sm"
                startContent={
                  <CheckCircle aria-hidden="true" className="w-3 h-3" />
                }
                variant="solid"
              >
                Completed
              </StatusChip>
            )}
          </div>
          {renderCategoryBadge(need.category, onCategoryClick)}
        </div>

        {/* Card Body with Essential Info */}
        <CardBody className={cn("py-3", SPACING.WITHIN_SECTION)}>
          <h3 className={titleClass} id={`${needId}-title`}>
            {need.title}
          </h3>

          <div className="flex items-center justify-between mb-2">
            <StatusChip
              aria-label={`Urgency: ${capitalizeFirstLetter(need.urgency)}`}
              role="status"
              size="sm"
              startContent={<Clock aria-hidden="true" className="w-3 h-3" />}
              status={need.urgency}
              variant="bordered"
            >
              {capitalizeFirstLetter(need.urgency)}
            </StatusChip>

            {/* Due Date Badge */}
            {daysRemaining !== null && (
              <StatusChip
                aria-label={getDueDateAriaLabel(daysRemaining)}
                color={getDueDateColor(daysRemaining)}
                role="status"
                size="sm"
                variant="flat"
              >
                {getDueDateLabel(daysRemaining)}
              </StatusChip>
            )}
          </div>

          {/* Brief Description */}
          <p className={descriptionClass} id={`${needId}-description`}>
            {need.description}
          </p>

          {/* Key Metadata */}
          {renderNeedMetadata(
            need,
            claimStats,
            isCompleted,
            needId,
            terminology,
          )}
        </CardBody>

        {/* Card Footer */}
        <CardFooter className="flex justify-end py-3">
          <Link
            aria-labelledby={`${needId}-title`}
            className="absolute inset-0"
            href={needDetailUrl}
            onClick={(e) => {
              if (isInteractiveTarget(e.target as HTMLElement)) {
                e.stopPropagation();
              } else {
                handleCardClick();
              }
            }}
          >
            <span className="sr-only">View details for {need.title}</span>
          </Link>
          <Button
            aria-label={`View details for ${need.title}`}
            className="relative z-10"
            color="primary"
            size="sm"
            variant={buttonVariant}
            onPress={() => {
              handleCardClick();
              window.location.href = needDetailUrl;
            }}
          >
            {isCompleted ? "View Details" : "View Details"}
          </Button>
        </CardFooter>
      </Card>
    </article>
  );
}

// Add named export to maintain backwards compatibility
export { NeedCard };
